import { useState, useEffect, useRef } from 'react';
import { Button } from '@perch/ui';
import { SUPABASE_FUNCTIONS_URL } from '../../lib/supabase';
import { posthog } from '../../lib/posthog';
import { AirlineLogo } from '../ui/AirlineLogo';
import { getAirlineName } from '../../lib/constants';
import type { Session } from '@supabase/supabase-js';
import type { Booking } from '../../lib/types';

interface PaymentStepProps {
  session: Session;
  /** Booking received during email forwarding step — shown as urgency card. */
  pendingBooking?: Partial<Booking> | null;
  /** Dev-only: skip card step entirely (for testing the flow). */
  onDevSkip?: () => void;
}

/**
 * Card collection step — the gate to active tracking.
 *
 * Shows the user's pending flight (if received) with "Add a card to start
 * watching" urgency. Explicitly states the 15% commission rate.
 * No skip button — card is mandatory (Uber model).
 */
export function PaymentStep({ session, pendingBooking, onDevSkip }: PaymentStepProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasTrackedView = useRef(false);

  useEffect(() => {
    if (!hasTrackedView.current) {
      posthog.capture('payment_step_viewed');
      hasTrackedView.current = true;
    }
  }, []);

  const handleAddCard = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/create-checkout-session`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ app_url: window.location.origin }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to start payment setup');
      }

      const { url } = await res.json();
      posthog.capture('payment_step_redirect');
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl sm:text-3xl font-serif text-stone-900 dark:text-stone-100 mb-4">
        One Last Step
      </h2>

      {/* Urgency card: show the user's pending flight */}
      {pendingBooking?.airline_code && (
        <div className="rounded-2xl bg-white dark:bg-stone-900 border border-stone-200/60 dark:border-stone-700/50 p-5 mb-6">
          <div className="flex items-center gap-4">
            <AirlineLogo code={pendingBooking.airline_code} size={40} />
            <div className="flex-1 min-w-0">
              <div className="font-mono font-semibold text-stone-900 dark:text-stone-100">
                {pendingBooking.pnr && <>{pendingBooking.pnr} · </>}
                {getAirlineName(pendingBooking.airline_code)}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                {/* Stone dot — not yet active (emerald = active) */}
                <span className="inline-flex rounded-full h-2 w-2 bg-stone-400 dark:bg-stone-500"></span>
                <span className="text-sm text-stone-500 dark:text-stone-400">
                  Add a card to start watching
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <p className="text-stone-600 dark:text-stone-400 mb-2">
        Perch checks prices automatically. When we find a lower fare and rebook your flight, we earn 15% of what you save. You keep the rest.
      </p>
      <p className="text-sm text-stone-400 dark:text-stone-500 mb-8">
        If we find nothing, you pay nothing. Ever.
      </p>

      {error && (
        <div className="rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 p-4 mb-6">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      <Button
        onClick={handleAddCard}
        disabled={loading}
        aria-label="Add payment card to activate flight tracking"
        variant="coral"
        fullWidth
      >
        {loading ? 'Connecting to Stripe\u2026' : 'Add Card'}
      </Button>

      {/* Dev-only: skip card step for testing */}
      {!import.meta.env.PROD && onDevSkip && (
        <button
          onClick={onDevSkip}
          className="w-full text-[10px] text-amber-500/50 hover:text-amber-500 transition-colors py-2 mt-3"
        >
          [dev] skip card and go to dashboard →
        </button>
      )}
    </div>
  );
}
